import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { fetchUserActivity, incrementDailyActivity, fetchActivityYears } from '@/api/supabase/activity';
import type { DailyActivity } from '@/api/supabase/activity';

export type { DailyActivity };

/** Record one answered question for today (async, DB-backed) */
export function useRecordActivity() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return async () => {
    if (!user?.id) return;
    try {
      await incrementDailyActivity(user.id);
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      queryClient.invalidateQueries({ queryKey: ['activity-years'] });
    } catch (e) {
      console.error('Failed to record activity:', e);
    }
  };
}

/** Get activity data for a specific year */
export function useActivityData(year: number) {
  const { user } = useAuth();

  const sinceDate = `${year}-01-01`;
  const untilDate = `${year}-12-31`;

  const { data = {} } = useQuery({
    queryKey: ['user-activity', user?.id, year],
    queryFn: () => fetchUserActivity(user!.id, sinceDate, untilDate),
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  return data;
}

/** Get years that have activity data */
export function useActivityYears() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['activity-years', user?.id],
    queryFn: () => fetchActivityYears(user!.id),
    enabled: !!user?.id,
    staleTime: 60_000,
  });
}
